import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";
import { Collection } from "@use/models";

import { api } from "@courses-models/institution/src/ts/config";

/**
 * Describes data returned by v1/Unic/Institution/Classes/{institutionNumber}
 * from Gyldendal Access Service.
 */
type v1_Unic_Institution_Classes_Response = {
  Name: string;
  StudentCount?: number;
}[];

/**
 * Describes data returned by v1/Unic/Institution/ClassesWithStudentCount/{institutionNumber}
 * from Gyldendal Access Service.
 */
type v1_Unic_Institution_ClassesWithStudentCount_Response = {
  Name: string;
  StudentCount: number;
}[];

/**
 * Specification of the data format available within this collection.
 */
type Class = {
  id: string;
  name: string;
};

/**
 * Specification of the data format available within this collection, when a
 * student count was requested for classes.
 */
type ClassWithStudentCount = Class & {
  studentCount: number;
};

/**
 * Memory cache of collections that have already been fetched by the client, so
 * that we don't need to fetch the same collections over and over again.
 */
const cache = new EntityStore<InstitutionClassCollection>({ ttl: 600 });

/**
 * Iterable collection of classes available on the given institution.
 */
export class InstitutionClassCollection<
  T extends Class = Class
> extends Collection<T> {
  /**
   * Specifies the id of the institution that this collection is associated
   * with.
   */
  public readonly institutionId: string;

  /**
   * Contains an internal set containing all items available within this
   * collection.
   */
  protected readonly collection: T[];

  /**
   * Cache collection of items when this collection is instantiated.
   */
  constructor(initial: { collection: T[]; institutionId: string }) {
    super();

    this.collection = initial.collection;
    this.institutionId = initial.institutionId;
  }

  /**
   * Fetches a collection of courses that was shared with this student, and
   * converts each course into a well-typed model.
   */
  public static async GET_BY_ID(
    institutionId: string
  ): Promise<InstitutionClassCollection>;

  public static async GET_BY_ID(
    institutionId: string,
    withStudentCount: true
  ): Promise<InstitutionClassCollection<ClassWithStudentCount>>;

  @nonConcurrent()
  public static async GET_BY_ID(
    institutionId: string,
    withStudentCount: boolean = false
  ): Promise<InstitutionClassCollection> {
    const cacheId = `${institutionId}/${withStudentCount}`;

    // If this collection has already been cached, then return an instance
    // of this method directly from there
    if (cache.has(cacheId)) {
      return <InstitutionClassCollection>cache.get(cacheId);
    }

    // Fetch data about the user from Gyldendal Access Service
    const endpoint = withStudentCount
      ? `${api.gyldendalAccessService(
          "v1"
        )}/api/v1/Unic/Institution/ClassesWithStudentCount/${encodeURIComponent(
          institutionId
        )}`
      : `${api.gyldendalAccessService(
          "v1"
        )}/api/v1/Unic/Institution/Classes/${encodeURIComponent(
          institutionId
        )}`;

    const response = withStudentCount
      ? await axios.get<v1_Unic_Institution_ClassesWithStudentCount_Response>(
          gGateway(endpoint)
        )
      : await axios.get<v1_Unic_Institution_Classes_Response>(gGateway(endpoint));

    // Format data into an array of class objects
    const models = response.data.map((item) => ({
      id: item.Name,
      name: item.Name,
      studentCount: withStudentCount ? item.StudentCount : undefined,
    }));

    // Create our collection
    const collection = new InstitutionClassCollection({
      collection: models,
      institutionId,
    });

    // Cache the collection for future use
    cache.set(cacheId, collection);

    // Return the collection publicly
    return collection;
  }
}
